import React, { useState } from 'react';
import Section from '../../components/layanan/secure_firewalll.jsx/section';
import { useDarkMode } from '../../components/darkmode/DarkModeContext';

import Footer from '../../components/footer/index';
import { Shield, Lock, Eye, Server, Zap, Users, CheckCircle, MessageCircle, ArrowRight, AlertTriangle, Activity, Globe, Clock, MapPin, TrendingUp, BarChart3, Layers } from 'lucide-react';

const SecureFirewallPage = () => {
  const { isDarkMode } = useDarkMode();
  const [activeTab, setActiveTab] = useState('personal');

  const firewallFeatures = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Advanced Threat Protection",
      description: "Perlindungan real-time terhadap malware, ransomware, dan advanced persistent threats (APT)",
      color: "bg-red-500"
    },
    {
      icon: <Activity className="w-8 h-8" />,
      title: "Deep Packet Inspection",
      description: "Analisis mendalam setiap paket data untuk mendeteksi ancaman tersembunyi dan anomali trafik",
      color: "bg-blue-500"
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: "Intrusion Detection & Prevention",
      description: "Sistem IDS/IPS terintegrasi untuk mendeteksi dan mencegah serangan cyber secara otomatis",
      color: "bg-purple-500"
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Web Application Firewall",
      description: "Perlindungan khusus untuk aplikasi web dari serangan OWASP Top 10 dan SQL injection",
      color: "bg-green-500"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Real-time Monitoring",
      description: "Dashboard monitoring 24/7 dengan alert system dan reporting komprehensif",
      color: "bg-yellow-500"
    },
    {
      icon: <Layers className="w-8 h-8" />,
      title: "Multi-Layer Security",
      description: "Arsitektur keamanan berlapis dari network hingga application layer",
      color: "bg-indigo-500"
    }
  ];

  const useCases = {
    personal: [
      {
        title: "Home Office Protection",
        description: "Lindungi jaringan rumah dan perangkat IoT dari serangan cyber dan akses tidak sah",
        image: "/image/home_firewall.png",
        benefits: ["Device protection", "IoT security", "Parental controls"]
      },
      {
        title: "Personal Data Security",
        description: "Keamanan data pribadi dan aktivitas online dari malware dan phishing",
        image: "/image/personal_security.png",
        benefits: ["Identity protection", "Safe browsing", "Privacy shield"]
      }
    ],
    business: [
      {
        title: "SME Network Security",
        description: "Solusi firewall untuk UKM dengan perlindungan menyeluruh dan mudah dikelola",
        image: "/image/business_firewall.png",
        benefits: ["Centralized management", "Employee access control", "Business continuity"]
      },
      {
        title: "Remote Work Security",
        description: "Keamanan untuk tim remote dengan VPN terintegrasi dan access control",
        image: "/image/remote_security.png",
        benefits: ["Secure remote access", "Policy enforcement", "Threat intelligence"]
      }
    ],
    enterprise: [
      {
        title: "Enterprise Security Operations",
        description: "SOC (Security Operations Center) dengan AI-powered threat detection",
        image: "/image/enterprise_soc.png",
        benefits: ["24/7 monitoring", "Incident response", "Compliance reporting"]
      },
      {
        title: "Multi-Site Protection",
        description: "Manajemen keamanan terpusat untuk multiple lokasi dan data center",
        image: "/image/multisite_security.png",
        benefits: ["Centralized policy", "Site-to-site protection", "Scalable architecture"]
      }
    ]
  };

  const firewallPackages = [
    {
      id: 1,
      name: "Personal Firewall",
      popular: false,
      color: "from-blue-500 to-blue-700",
      devices: "5 Devices",
      protection: "Basic Protection",
      bandwidth: "100 Mbps",
      features: [
        "Stateful Packet Filtering",
        "Basic Intrusion Detection",
        "Web Content Filtering",
        "Anti-Malware Protection",
        "Email Support",
        "Monthly Reports"
      ]
    },
    {
      id: 2,
      name: "Business Firewall",
      popular: true,
      color: "from-green-500 to-green-700",
      devices: "50 Devices",
      protection: "Advanced Protection",
      bandwidth: "1 Gbps",
      features: [
        "All Personal Features",
        "Deep Packet Inspection",
        "Advanced Threat Protection",
        "Application Control",
        "VPN Integration",
        "24/7 Support",
        "Real-time Monitoring",
        "Custom Security Policies"
      ]
    },
    {
      id: 3,
      name: "Enterprise Firewall",
      popular: false,
      color: "from-purple-500 to-purple-700",
      devices: "Unlimited",
      protection: "Enterprise Grade",
      bandwidth: "10+ Gbps",
      features: [
        "All Business Features",
        "AI-Powered Threat Detection",
        "Zero-Day Protection",
        "Security Orchestration",
        "Compliance Management",
        "Dedicated SOC Team",
        "Custom Integration",
        "SLA 99.99% Uptime"
      ]
    }
  ];

  const threatStatistics = [
    { threat: "Malware Attacks", blocked: "99.8%", color: "text-red-500" },
    { threat: "Phishing Attempts", blocked: "99.5%", color: "text-orange-500" },
    { threat: "DDoS Attacks", blocked: "99.9%", color: "text-purple-500" },
    { threat: "Data Breaches", prevented: "100%", color: "text-green-500" },
    { threat: "Unauthorized Access", blocked: "99.7%", color: "text-blue-500" },
    { threat: "SQL Injections", prevented: "100%", color: "text-indigo-500" }
  ];

  const testimonials = [
    {
      name: "Ahmad Rizki",
      role: "IT Security Manager",
      company: "Bank Regional",
      rating: 5,
      comment: "Firewall enterprise ini sangat powerful. Berhasil mencegah berbagai serangan cyber dan memberikan visibilitas penuh terhadap traffic jaringan kami.",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face"
    },
    {
      name: "Sari Indrawati",
      role: "CISO",
      company: "Healthcare Provider",
      rating: 5,
      comment: "Sistem monitoring 24/7 dan response time yang cepat membuat kami tenang. Data pasien terlindungi dengan baik dari ancaman cyber.",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=60&h=60&fit=crop&crop=face"
    },
    {
      name: "PT. Teknologi Maju",
      role: "CTO",
      company: "Fintech Startup",
      rating: 5,
      comment: "Implementasi mudah dan dukungan teknis yang excellent. Firewall ini scale dengan pertumbuhan bisnis kami tanpa masalah.",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face"
    }
  ];

  // Fungsi untuk mengarahkan ke WhatsApp dengan pesan otomatis
  const contactViaWhatsApp = (packageName = "Secure Firewall Service") => {
    const phoneNumber = "6287778882022";
    const message = `Halo, saya tertarik dengan layanan ${packageName}. Bisakah saya mendapatkan informasi lebih lanjut mengenai harga dan fiturnya?`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'
    }`}>

      
      
      {/* Hero Section */}
      <Section className="relative overflow-hidden px-4 py-12 md:py-16 mb-0" />

      {/* Threat Statistics Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Statistik Perlindungan Ancaman
            </h2>
            <p className={`text-lg ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Tingkat perlindungan yang telah kami capai untuk klien enterprise
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {threatStatistics.map((stat, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl border-2 transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 shadow-gray-900/50 hover:border-[#1E9B4C]' 
                    : 'bg-white border-gray-200 shadow-gray-300/20 hover:border-[#1E9B4C]'
                }`}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {stat.threat}
                  </h3>
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                </div>
                <div className="text-right">
                  <span className={`text-2xl font-bold ${stat.color}`}>
                    {stat.blocked || stat.prevented}
                  </span>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {stat.blocked ? 'Diblokir' : 'Dicegah'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Fitur Firewall Unggulan
            </h2>
            <p className={`text-lg ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Teknologi keamanan terdepan dengan perlindungan berlapis dan monitoring 24/7
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#1E9B4C] to-green-600 mx-auto rounded-full mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {firewallFeatures.map((feature, index) => (
              <div
                key={index}
                className={`group p-6 rounded-xl shadow-lg overflow-hidden transform hover:scale-105 hover:shadow-2xl transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-800 border border-gray-700 hover:border-[#1E9B4C] shadow-gray-900/50' 
                    : 'bg-white border border-gray-100 hover:border-[#1E9B4C] shadow-gray-300/20'
                }`}
              >
                <div className={`w-16 h-16 rounded-lg flex items-center justify-center text-white mb-4 ${
                  feature.color || 'bg-[#1E9B4C]'
                }`}>
                  {feature.icon}
                </div>

                <h3 className={`text-xl font-bold mb-3 transition-colors ${
                  isDarkMode
                    ? 'text-white group-hover:text-[#1E9B4C]'
                    : 'text-gray-900 group-hover:text-[#1E9B4C]'
                }`}>
                  {feature.title}
                </h3>

                <p className={`transition-colors ${
                  isDarkMode 
                    ? 'text-gray-300 group-hover:text-gray-200' 
                    : 'text-gray-600 group-hover:text-gray-700'
                }`}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Solusi Keamanan untuk Setiap Kebutuhan
            </h2>
            <p className={`text-lg ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Temukan solusi firewall yang tepat untuk kebutuhan spesifik Anda
            </p>
          </div>

          <div className="flex justify-center mb-8">
            <div className={`inline-flex rounded-lg p-1 shadow-lg ${
              isDarkMode 
                ? 'bg-gray-800 border border-gray-700' 
                : 'bg-white border border-gray-200'
            }`}>
              {Object.keys(useCases).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    activeTab === tab
                      ? 'bg-[#1E9B4C] text-white shadow-lg'
                      : isDarkMode
                      ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {tab === 'personal'
                    ? 'Personal'
                    : tab === 'business'
                    ? 'Business'
                    : 'Enterprise'}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases[activeTab].map((useCase, index) => (
              <div
                key={index}
                className={`rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-800 border border-gray-700 shadow-gray-900/50' 
                    : 'bg-white border border-gray-100 shadow-gray-300/20'
                }`}
              >
                <div className="relative">
                  <img
                    src={useCase.image}
                    alt={useCase.title}
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1563206767-5b18f218e8de?w=400&h=200&fit=crop&crop=center';
                    }}
                  />
                  <div className={`absolute inset-0 ${
                    isDarkMode 
                      ? 'bg-gradient-to-t from-gray-800/20 to-transparent' 
                      : 'bg-gradient-to-t from-white/20 to-transparent'
                  }`}></div>
                </div>
                <div className="p-6">
                  <h3 className={`text-xl font-bold mb-3 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {useCase.title}
                  </h3>
                  <p className={`mb-4 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {useCase.description}
                  </p>
                  <div className="space-y-2">
                    {useCase.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#1E9B4C] flex-shrink-0" />
                        <span className={`text-sm ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {benefit}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Pilih Solusi Keamanan Anda
            </h2>
            <p className={`text-lg ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Paket firewall yang dapat disesuaikan dengan kebutuhan dan skala bisnis
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {firewallPackages.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative rounded-2xl p-8 shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                  pkg.popular
                    ? `bg-gradient-to-br ${pkg.color} text-white shadow-2xl transform scale-105`
                    : isDarkMode
                    ? 'bg-gray-800 text-white border border-gray-700 shadow-gray-900/50'
                    : 'bg-white text-gray-900 border border-gray-200 shadow-gray-300/20'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                  <div className="mb-4">
                    <span className="text-lg font-medium">Konsultasi untuk penawaran terbaik</span>
                  </div>
                </div>

                <div className="mb-8">
                  <div className={`flex justify-between mb-2 pb-2 border-b ${
                    pkg.popular 
                      ? 'border-white/20' 
                      : isDarkMode 
                      ? 'border-gray-700' 
                      : 'border-gray-200'
                  }`}>
                    <span>Devices</span>
                    <span className="font-bold">{pkg.devices}</span>
                  </div>
                  <div className={`flex justify-between mb-2 pb-2 border-b ${
                    pkg.popular 
                      ? 'border-white/20' 
                      : isDarkMode 
                      ? 'border-gray-700' 
                      : 'border-gray-200'
                  }`}>
                    <span>Protection Level</span>
                    <span className="font-bold">{pkg.protection}</span>
                  </div>
                  <div className={`flex justify-between mb-4 pb-2 border-b ${
                    pkg.popular 
                      ? 'border-white/20' 
                      : isDarkMode 
                      ? 'border-gray-700' 
                      : 'border-gray-200'
                  }`}>
                    <span>Throughput</span>
                    <span className="font-bold">{pkg.bandwidth}</span>
                  </div>

                  <ul className="space-y-3">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          pkg.popular ? 'text-white' : 'text-[#1E9B4C]'
                        }`} />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => contactViaWhatsApp(pkg.name)}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 transform hover:scale-105 ${
                    pkg.popular 
                      ? 'bg-white text-green-700 hover:bg-gray-100 shadow-lg' 
                      : 'bg-[#1E9B4C] text-white hover:bg-green-700 shadow-lg hover:shadow-xl'
                  }`}
                >
                  <MessageCircle className="w-5 h-5" />
                  Hubungi Kami
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-[#1E9B4C]'
      }`}>
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Lindungi Sistem Anda Sekarang Juga
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-3xl mx-auto">
            Jangan biarkan cyber threats mengganggu bisnis Anda. Dapatkan konsultasi gratis 
            dan implementasi firewall enterprise-grade hari ini.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => contactViaWhatsApp("Firewall Consultation")}
              className="bg-white text-[#1E9B4C] hover:bg-gray-100 font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <MessageCircle className="w-5 h-5" />
              Konsultasi Gratis
            </button>
            <button 
              onClick={() => contactViaWhatsApp("Firewall Enterprise")}
              className="border-2 border-white text-white hover:bg-white hover:text-[#1E9B4C] font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <ArrowRight className="w-5 h-5" />
              Enterprise Solution
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SecureFirewallPage;